import ErrorCircleSvg from "@/src/assets/icons/icon-error-circle.svg";
import PaperAirplaneSvg from "@/src/assets/icons/icon-paper-airplane.svg";
import { Button } from "@/src/components/atom/Button";
import TextButton from "@/src/components/atom/Button/TextButton";
import Icon from "@/src/components/atom/Icon";
import Input from "@/src/components/atom/Input";
import Typo from "@/src/components/atom/Typo";
import PublicPageHeader from "@/src/components/organism/PublicPageHeader";
import PublicPageFooter from "@/src/components/molecule/PublicPageFooter";
import useAlert from "@/src/hooks/useAlert";
import PUBLIC_PATH from "@/src/routes/public/path";
import { useResetPasswordMutation } from "@/src/store/apis/auth";
import colorSet from "@/src/styles/color";
import typo from "@/src/styles/typography";
import { emailRegex } from "@/src/utils/regex";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { styled } from "styled-components";

const FindPasswordPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [resetPassword] = useResetPasswordMutation();
  const nameRef = useRef<HTMLInputElement>(null);
  const secondStepButtonRef = useRef<HTMLButtonElement>(null);
  const alert = useAlert();
  const [step, setStep] = useState(1);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      name: "",
      id: "",
    },
  });

  const handlePasswordReset = async () => {
    try {
      await resetPassword({
        name: watch("name"),
        aId: watch("id"),
      }).unwrap();

      setStep(2);
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ message, type: "error" });
    }
  };

  useEffect(() => {
    if (step === 1) {
      if (nameRef.current) {
        nameRef.current.focus();
      }
    } else {
      if (secondStepButtonRef.current) {
        secondStepButtonRef.current.focus();
      }
    }
  }, [step]);

  const goTo = (path: keyof typeof PUBLIC_PATH) => {
    navigate(PUBLIC_PATH[path]);
  };

  const renderContent = () => {
    switch (step) {
      case 1:
        return (
          <StyledForm onSubmit={handleSubmit(handlePasswordReset)}>
            <InputFieldsSection>
              <FormField>
                <Typo typoType="b7m" as="label">
                  {t("findPassword:fullName")} <Required>*</Required>
                </Typo>
                <Controller
                  name="name"
                  rules={{
                    required: true,
                  }}
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      ref={nameRef}
                      data-invalid={!!errors.name}
                      id="name"
                      placeholder={t("findPassword:fullNamePlaceholder")}
                      onClear={() => setValue("name", "")}
                    />
                  )}
                />

                {errors.name?.type === "required" && (
                  <FormErrorMessage>
                    <Icon iconSrc={ErrorCircleSvg} iconSize={16} />
                    {t("error:required")}
                  </FormErrorMessage>
                )}
              </FormField>

              <FormField>
                <Typo typoType="b7m" as="label">
                  {t("findPassword:id")} <Required>*</Required>
                </Typo>
                <Controller
                  name="id"
                  rules={{
                    required: true,
                    pattern: emailRegex,
                  }}
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      data-invalid={!!errors.id}
                      id="id"
                      placeholder={t("findPassword:idPlaceholder")}
                      onClear={() => setValue("id", "")}
                    />
                  )}
                />

                {errors.id?.type === "required" && (
                  <FormErrorMessage>
                    <Icon iconSrc={ErrorCircleSvg} iconSize={16} />
                    {t("error:required")}
                  </FormErrorMessage>
                )}
                {errors.id?.type === "pattern" && (
                  <FormErrorMessage>
                    <Icon iconSrc={ErrorCircleSvg} iconSize={16} />
                    {t("error:login.emailTypeMissMatching")}
                  </FormErrorMessage>
                )}
              </FormField>
            </InputFieldsSection>

            <ButtonSection>
              <SubmitButton type="submit">
                <Typo typoType="btn3m" color="white">
                  {t("findPassword:resetPasswordButtonLabel")}
                </Typo>
              </SubmitButton>

              <LoginBackSection>
                <Typo typoType="b9m">{t("findPassword:rememberPassword")}</Typo>
                <TextButton onClick={() => goTo("LOGIN")}>
                  <Typo typoType="b9m" color="systemBlue2">
                    {t("findPassword:backToLogin")}
                  </Typo>
                </TextButton>
              </LoginBackSection>
            </ButtonSection>
          </StyledForm>
        );
      case 2:
        return (
          <CompleteCard>
            <IconContainer>
              <Icon iconSrc={PaperAirplaneSvg} iconSize={80} />
            </IconContainer>

            <CompleteTextSection>
              <Typo typoType="d6" color="gray1">
                {t("findPassword:resetCompleteTitle")}
              </Typo>
              <Typo typoType="b7m" color="gray2">
                {t("findPassword:resetCompleteDescription")}
              </Typo>
            </CompleteTextSection>

            <BackToLoginButton
              buttonGrade="secondary"
              onClick={() => goTo("LOGIN")}
              ref={secondStepButtonRef}
            >
              <Typo typoType="btn3m" color="indigo">
                {t("findPassword:backToLogin")}
              </Typo>
            </BackToLoginButton>
          </CompleteCard>
        );
    }
  };

  return (
    <Layout>
      <PublicPageHeader />

      <FormSection>
        <HeaderSection>
          <PageTitle>
            <Typo typoType="d3" as="h1" color="gray1">
              {step === 1
                ? t("findPassword:resetPasswordTitle")
                : t("findPassword:resetComplete")}
            </Typo>
          </PageTitle>
          {step === 1 && (
            <SubTitle>
              <Typo typoType="b6r" color="gray4">
                {t("findPassword:resetPasswordDescription")}
              </Typo>
            </SubTitle>
          )}
        </HeaderSection>

        {renderContent()}
      </FormSection>

      <PublicPageFooter />
    </Layout>
  );
};

export default FindPasswordPage;

const Layout = styled.main`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  background: ${colorSet.white};
`;

const FormSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  padding: 60px 40px;
  flex: 1;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const PageTitle = styled.div`
  padding: 24px 0;
  text-align: center;
  width: 100%;

  h1 {
    margin: 0;
  }
`;

const SubTitle = styled.div`
  text-align: center;
  padding: 8px 0;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 40px;
  width: 100%;
  max-width: 520px;
`;

const InputFieldsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ButtonSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Required = styled.span`
  color: ${colorSet.red2};
`;

const FormErrorMessage = styled.span`
  ${typo.b9r}
  color: ${colorSet.red2};
  padding-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SubmitButton = styled(Button)`
  text-align: center;
  width: 100%;
`;

const LoginBackSection = styled.div`
  padding: 16px 0;
  display: flex;
  gap: 14px;
  justify-content: center;
`;

const CompleteCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  width: 100%;
  max-width: 720px;
  padding: 48px;
  background: ${colorSet.white};
  border-radius: 16px;
  box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.1),
    0px 4px 16px rgba(0, 0, 0, 0.1);
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CompleteTextSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
  white-space: pre-wrap;
`;

const BackToLoginButton = styled(Button)`
  text-align: center;
  width: 100%;
`;
